import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useMemo } from 'react';

import { EmptyStateWithLoader } from '../../components/empty-state';
import CommonTable from '../../components/tableComponent/commonTableComponent';
import PaymentInvoiceAccordionContent from '../../components/tableComponent/PaymentInvoiceAccordionContent';
import { PaymentHistoryItemNew, PaymentInvoiceDetail } from '../../services/payment-services';
import { formatCurrency, formatToUSDate } from '../../utils/helper';
import { getEqualWidthTableSx, mapPaymentHistoryRow, sortByDateDesc } from '../../utils/paymentHistoryHelpers';
import { useTranslation } from '../../utils/translations';

import { PaymentHistoryMobileCard } from './components/PaymentHistoryMobileCard';

interface PaymentHistoryProps {
    status?: boolean;
    contractId?: string | null | undefined | number;
    data?: PaymentHistoryItemNew[];
    loading?: boolean;
    currentPage?: number;
    rowsPerPage?: number;
    searchTerm?: string;
    onSearchChange?: (value: string) => void;
    onTotalCountChange?: (count: number) => void;
    /** Mobile/tablet-only: sum of the currently filtered payments' amounts, for the "Total Processed" summary. */
    onTotalAmountChange?: (total: number) => void;
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({
    data = [],
    loading = false,
    currentPage = 0,
    rowsPerPage = 10,
    searchTerm = '',
    onTotalCountChange,
    onTotalAmountChange,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const searchLower = (searchTerm || '').trim().toLowerCase();

    const payments = useMemo(() => {
        if (!data || data.length === 0) return [];

        const sortedPayments = sortByDateDesc(data, payment => payment.paymentDate);

        return sortedPayments.map((payment: PaymentHistoryItemNew) => ({
            ...mapPaymentHistoryRow(payment, formatToUSDate, formatCurrency),
            paymentDateISO: payment.paymentDate || '', // Store ISO date for reliable sorting
        }));
    }, [data]);

    const filteredPayments = useMemo(() => {
        if (!searchLower) return payments;
        return payments.filter(row => {
            const contractId = String(row.contractId || '').toLowerCase();
            const paymentDate = String(row.paymentDate || '').toLowerCase();
            const invoiceNumber = String(row.invoiceNumber || '').toLowerCase();
            const referenceNumber = String(row.referenceNumber || '').toLowerCase();
            const paymentType = String(row.paymentType || '').toLowerCase();
            const amount = String(row.amount || '').toLowerCase();
            const statusVal = String(row.status || '').toLowerCase();
            return [contractId, paymentDate, invoiceNumber, referenceNumber, paymentType, amount, statusVal].some(
                part => part.includes(searchLower),
            );
        });
    }, [payments, searchLower]);

    useEffect(() => {
        onTotalCountChange?.(filteredPayments.length);
    }, [filteredPayments.length, onTotalCountChange]);

    useEffect(() => {
        if (!onTotalAmountChange) return;
        const total = filteredPayments.reduce((sum, row) => sum + (Number(row.originalPayment?.amount) || 0), 0);
        onTotalAmountChange(total);
    }, [filteredPayments, onTotalAmountChange]);

    const paginatedPayments = useMemo(() => {
        const startIndex = currentPage * rowsPerPage;
        const endIndex = rowsPerPage === -1 ? filteredPayments.length : startIndex + rowsPerPage;
        return filteredPayments.slice(startIndex, endIndex);
    }, [filteredPayments, currentPage, rowsPerPage]);

    const error = null;

    const equalMinWidth = 120;
    const columns = [
        {
            id: 'contractId',
            label: t('payments.upcomingPayments.contractId') || 'Contract ID',
            sortable: false,
            minWidth: equalMinWidth,
        },
        {
            id: 'paymentDate',
            label: t('contracts.paymentHistory.paymentEffectiveDate') || 'Payment Effective Date',
            sortable: true,
            minWidth: 150,
        },
        {
            id: 'referenceNumber',
            label: t('contracts.paymentHistory.referenceNumber') || 'Reference Number',
            minWidth: 150,
        },
        {
            id: 'paymentType',
            label: t('contracts.paymentHistory.method') || 'Method',
            align: 'center' as const,
            minWidth: equalMinWidth,
        },
        {
            id: 'amount',
            label: t('contracts.paymentHistory.amount') || 'Amount',
            align: 'right' as const,
            minWidth: equalMinWidth,
        },
    ];

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const isEmpty = (payments.length === 0 || (filteredPayments.length === 0 && Boolean(searchLower))) && !loading;

    if (loading || isEmpty) {
        return <EmptyStateWithLoader loading={loading} empty={isEmpty} emptyMessage={t('common.NoRecordsAvailable')} />;
    }

    const renderAccordionContent = (row: Record<string, unknown>) => (
        <PaymentInvoiceAccordionContent invoices={row.invoices as PaymentInvoiceDetail[]} withTitleTooltips />
    );

    if (isMobileOrTablet) {
        return (
            <Stack sx={{ flex: 1, minHeight: 0, px: { xs: 1.5, sm: 3 } }} gap={1}>
                {paginatedPayments.map((row, rowIndex) => (
                    <PaymentHistoryMobileCard key={rowIndex} row={row} />
                ))}
            </Stack>
        );
    }

    return (
        <Stack sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 3 }}>
            <CommonTable
                columns={columns}
                rows={paginatedPayments}
                enableAccordion={true}
                renderAccordionContent={renderAccordionContent}
                stickyHeader={true}
                maxHeight="100%"
                className="accordionTableWrapper"
                sx={getEqualWidthTableSx(columns.length)}
            />
        </Stack>
    );
};

export default PaymentHistory;
